import { memo, useState, useCallback } from 'react';

import { StepperNav } from './StepperNav';
import { TechnicalSpecsSection } from './TechnicalSpecsSection';
import { LocationSection } from './LocationSection';
import { ScheduleSection } from './ScheduleSection';
import { ActionsSidebar } from './ActionsSidebar';
import { STEPS, useLocalizedSteps, type StationFormData } from '../stationForm';

interface StationFormWizardProps {
  mode: 'create' | 'edit';
  initialData: StationFormData;
  /** Layout class of the sections+sidebar wrapper (create vs edit differ). */
  contentClassName: string;
  /** Perform the API call. Throw to surface an error in the sidebar. */
  onSubmit: (formData: StationFormData) => Promise<void>;
  /** Map a thrown submit error to the message shown in the sidebar. */
  getErrorMessage: (err: unknown) => string;
  /**
   * Custom cancel behavior (e.g. navigate away). When omitted the wizard
   * resets to `initialData` and returns to the first step.
   */
  onCancel?: () => void;
}

/**
 * Shared step wizard used by both CreateStation and the EditStationTab:
 * owns step state, navigation, form state, submit lifecycle and the
 * StepperNav + sections + ActionsSidebar layout.
 *
 * Create mode freezes the form once submission succeeds (the page is about
 * to redirect); edit mode stays editable and clears the success banner on
 * the next change.
 */
export const StationFormWizard = memo(function StationFormWizard({
  mode,
  initialData,
  contentClassName,
  onSubmit,
  getErrorMessage,
  onCancel,
}: StationFormWizardProps) {
  const localizedSteps = useLocalizedSteps();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<StationFormData>(initialData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSuccess, setIsSuccess] = useState(false);

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === STEPS.length - 1;
  // Create mode locks the form after success; edit mode stays editable.
  const isFrozen = mode === 'create' && isSuccess;

  const handleFieldChange = useCallback(
    <K extends keyof StationFormData>(field: K, value: StationFormData[K]) => {
      if (isFrozen) return;
      setFormData((prev) => ({ ...prev, [field]: value }));
      setError(null);
      if (mode === 'edit') setIsSuccess(false);
    },
    [isFrozen, mode]
  );

  const handleStepClick = useCallback((stepIndex: number) => {
    if (isFrozen) return;
    setCurrentStep(stepIndex);
  }, [isFrozen]);

  const handleNext = useCallback(() => {
    if (!isLastStep && !isFrozen) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [isLastStep, isFrozen]);

  const handlePrevious = useCallback(() => {
    if (!isFirstStep && !isFrozen) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [isFirstStep, isFrozen]);

  const handleCancel = useCallback(() => {
    if (onCancel) {
      onCancel();
      return;
    }
    // Default: reset form to the original data
    setFormData(initialData);
    setCurrentStep(0);
    setError(null);
    setIsSuccess(false);
  }, [onCancel, initialData]);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);
    if (mode === 'edit') setIsSuccess(false);

    try {
      await onSubmit(formData);
      setIsSuccess(true);
    } catch (err) {
      console.error('Failed to submit station form:', err);
      setError(getErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  }, [mode, formData, onSubmit, getErrorMessage]);

  // Render current step section
  const renderCurrentSection = () => {
    switch (currentStep) {
      case 0:
        return (
          <TechnicalSpecsSection
            formData={formData}
            onFieldChange={handleFieldChange}
            hideIdTag={mode === 'edit'}
          />
        );
      case 1:
        return (
          <LocationSection
            formData={formData}
            onFieldChange={handleFieldChange}
          />
        );
      case 2:
        return (
          <ScheduleSection
            formData={formData}
            onFieldChange={handleFieldChange}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      {/* Stepper Navigation */}
      <StepperNav
        steps={localizedSteps}
        currentStep={currentStep}
        onStepClick={handleStepClick}
      />

      {/* Main Content */}
      <div className={contentClassName}>
        <div className="form-sections">
          {renderCurrentSection()}
        </div>

        <ActionsSidebar
          currentStep={currentStep}
          totalSteps={STEPS.length}
          onNext={handleNext}
          onPrevious={handlePrevious}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isSubmitting={isSubmitting}
          isFirstStep={isFirstStep}
          isLastStep={isLastStep}
          error={error}
          isSuccess={isSuccess}
          mode={mode}
        />
      </div>
    </>
  );
});

export default StationFormWizard;
